import * as React from "react";
import { useNotify } from "react-admin";
import { useAuth } from "@airborne/react-admin";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import { Chip } from "@material-ui/core";
import Autorenew from "@material-ui/icons/Autorenew";
import CheckCircle from "@material-ui/icons/CheckCircle";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import HelpOutline from "@material-ui/icons/HelpOutline";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import { useAppDataProvider } from "../../../dataprovider";
import { useFrBulkFetchStatusListener } from "../../../Helper/useFrBulkFetchStatusListener";

const MONTH_NAMES = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

interface FrBulkFetchRequest {
  id: number;
  targetYear: number;
  targetMonth: number;
  requestedYear: number;
  requestedMonth: number;
  frBulkFetchRequestStatus: 0 | 1 | 2 | 3;
  errorMessage: string | null;
}

const STATUS_LABELS: Record<number, string> = {
  0: "Pending",
  1: "InProgress",
  2: "Completed",
  3: "Failed",
};

const STATUS_COLORS: Record<number, string> = {
  0: "#ff9800",
  1: "#2196f3",
  2: "#4caf50",
  3: "#f44336",
};

const STATUS_ICONS: Record<number, React.ReactElement> = {
  0: <HourglassEmpty style={{ color: "#fff" }} />,
  1: <Autorenew style={{ color: "#fff" }} />,
  2: <CheckCircle style={{ color: "#fff" }} />,
  3: <ErrorOutline style={{ color: "#fff" }} />,
};

const FrBulkFetchPage: React.FC = () => {
  const notify = useNotify();
  const dataProvider = useAppDataProvider();
  const { tokens } = useAuth();

  const [startYear, setStartYear] = React.useState<number>(currentYear);
  const [startMonth, setStartMonth] = React.useState<number>(1);
  const [endYear, setEndYear] = React.useState<number>(currentYear);
  const [endMonth, setEndMonth] = React.useState<number>(currentMonth);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [isListLoading, setIsListLoading] = React.useState<boolean>(false);
  const [requests, setRequests] = React.useState<FrBulkFetchRequest[]>([]);

  const isEndBeforeStart = endYear < startYear || (endYear === startYear && endMonth < startMonth);

  const loadRequests = React.useCallback(async () => {
    setIsListLoading(true);
    try {
      const response = await dataProvider.getFrBulkFetchRequests(currentYear, currentMonth);
      if (response?.data?.length > 0) {
        const sorted = ([...response.data] as FrBulkFetchRequest[]).sort(
          (a, b) => a.targetYear - b.targetYear || a.targetMonth - b.targetMonth
        );
        setRequests(sorted as any);
      } else {
        setRequests([]);
      }
    } catch {
      setRequests([]);
    } finally {
      setIsListLoading(false);
    }
  }, [dataProvider]);

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useFrBulkFetchStatusListener(tokens?.accessToken ?? "", payload => {
    setRequests(prev =>
      prev.map(req =>
        req.targetYear === payload.year && req.targetMonth === payload.month
          ? { ...req, frBulkFetchRequestStatus: payload.status as 0 | 1 | 2 | 3, errorMessage: payload.errorMessage }
          : req
      )
    );
  });

  const handleEnqueue = async () => {
    if (isEndBeforeStart) return;
    setIsSubmitting(true);
    try {
      await dataProvider.postFrBulkFetchEnqueue({ startYear, startMonth, endYear, endMonth });
      notify("FR veri talebi başarıyla kuyruğa alındı.", "success");
      await loadRequests();
    } catch (error: any) {
      const message = error?.body?.message || error?.message || (typeof error === "string" ? error : "Bilinmeyen hata");
      notify(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3} gridGap={8}>
        <CloudDownloadIcon color="primary" />
        <Typography variant="h5">FR Bulk Fetch Yönetimi</Typography>
      </Box>

      {/* Form */}
      <Paper elevation={2}>
        <Box p={3}>
          <Typography variant="subtitle1" gutterBottom>
            Dönem Aralığı Seçin
          </Typography>

          <Box display="flex" alignItems="flex-end" flexWrap="wrap" gridGap={16} mt={2}>
            {/* Başlangıç */}
            <Box>
              <Typography variant="caption" color="textSecondary">
                Başlangıç Dönemi
              </Typography>
              <Box display="flex" gridGap={8} mt={0.5}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                  <InputLabel>Yıl</InputLabel>
                  <Select
                    value={startYear}
                    onChange={e => setStartYear(Number(e.target.value))}
                    label="Yıl"
                    disabled={isSubmitting}
                  >
                    {YEARS.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Ay</InputLabel>
                  <Select
                    value={startMonth}
                    onChange={e => setStartMonth(Number(e.target.value))}
                    label="Ay"
                    disabled={isSubmitting}
                  >
                    {MONTHS.map(m => (
                      <MenuItem key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Typography variant="h6" style={{ paddingBottom: 4 }}>
              —
            </Typography>

            {/* Bitiş */}
            <Box>
              <Typography variant="caption" color="textSecondary">
                Bitiş Dönemi
              </Typography>
              <Box display="flex" gridGap={8} mt={0.5}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                  <InputLabel>Yıl</InputLabel>
                  <Select
                    value={endYear}
                    onChange={e => setEndYear(Number(e.target.value))}
                    label="Yıl"
                    disabled={isSubmitting}
                  >
                    {YEARS.map(y => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Ay</InputLabel>
                  <Select
                    value={endMonth}
                    onChange={e => setEndMonth(Number(e.target.value))}
                    label="Ay"
                    disabled={isSubmitting}
                  >
                    {MONTHS.map(m => (
                      <MenuItem key={m} value={m}>
                        {MONTH_NAMES[m - 1]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleEnqueue}
              disabled={isSubmitting || isEndBeforeStart}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
              style={{ alignSelf: "flex-end", height: 40 }}
            >
              {isSubmitting ? "Kuyruğa Alınıyor..." : "FR Verilerini Kuyruğa Al"}
            </Button>
          </Box>

          {isEndBeforeStart && (
            <Typography variant="caption" color="error" style={{ display: "block", marginTop: 8 }}>
              Bitiş dönemi başlangıç döneminden önce olamaz.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Liste */}
      <Box mt={3}>
        <Paper elevation={2}>
          <Box p={2}>
            <Typography variant="subtitle1" gutterBottom>
              Bu Aya Ait FR Veri Talepleri ({MONTH_NAMES[currentMonth - 1]} {currentYear})
            </Typography>

            {isListLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : requests.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ padding: "16px 0" }}>
                Bu ay henüz FR veri talebi oluşturulmamış.
              </Typography>
            ) : (
              <List dense>
                {requests.map(req => (
                  <ListItem key={req.id} divider>
                    <ListItemText
                      primary={`${MONTH_NAMES[req.targetMonth - 1]} ${req.targetYear}`}
                      secondary={
                        req.frBulkFetchRequestStatus === 3 && req.errorMessage ? `Hata: ${req.errorMessage}` : undefined
                      }
                      secondaryTypographyProps={{
                        style: { color: "#f44336" },
                      }}
                    />
                    <Chip
                      icon={STATUS_ICONS[req.frBulkFetchRequestStatus] ?? <HelpOutline style={{ color: "#fff" }} />}
                      label={STATUS_LABELS[req.frBulkFetchRequestStatus] ?? "Bilinmiyor"}
                      size="small"
                      style={{
                        backgroundColor: STATUS_COLORS[req.frBulkFetchRequestStatus] ?? "#9e9e9e",
                        color: "#fff",
                        marginLeft: 12,
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ marginLeft: 16, whiteSpace: "nowrap" }}
                    >
                      {`Talep: ${MONTH_NAMES[req.requestedMonth - 1]} ${req.requestedYear}`}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default FrBulkFetchPage;
